<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ArrowRight, RefreshCw } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useProcurementApi } from '../composables/useProcurementApi'
import ListPagination from '../components/ListPagination.vue'
import { OPEN_PURCHASE_ORDER_STATUSES, deliveryUrgency, purchaseOrderStatusClass } from '../types/procurement'
import type { PurchaseOrder } from '../types/procurement'

const { t } = useI18n()
const api = useProcurementApi()

const orders = ref<PurchaseOrder[]>([])
const loading = ref(true)
const error = ref('')
const search = ref('')
const openOnly = ref(true)
const page = ref(1)
const perPage = ref(20)
const today = new Date()

let searchTimer: ReturnType<typeof setTimeout> | undefined

/** Only the newest page is guaranteed by the API, so each status is queried. */
async function loadOrders() {
  const statuses = openOnly.value ? OPEN_PURCHASE_ORDER_STATUSES : [...OPEN_PURCHASE_ORDER_STATUSES, 'received']
  const term = search.value.trim() || undefined

  const pages = await Promise.all(
    statuses.map((status) => api.listPurchaseOrders({ status, search: term, per_page: 100 })),
  )

  const byId = new Map<number, PurchaseOrder>()
  for (const page of pages) {
    for (const order of page.items) byId.set(order.id, order)
  }

  orders.value = [...byId.values()]
}

watch(openOnly, () => {
  page.value = 1
  void load()
})

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    page.value = 1
    void load()
  }, 300)
})

watch(perPage, () => {
  page.value = 1
})

const visible = computed(() => {
  return orders.value
    .sort((a, b) => {
      const left = a.expected_date ?? '9999-12-31'
      const right = b.expected_date ?? '9999-12-31'
      return left.localeCompare(right)
    })
})

const totalPages = computed(() => Math.max(1, Math.ceil(visible.value.length / perPage.value)))
const paged = computed(() =>
  visible.value.slice((page.value - 1) * perPage.value, page.value * perPage.value),
)

function urgency(order: PurchaseOrder): 'overdue' | 'due' | null {
  return deliveryUrgency(order, today)
}

function money(value: number, currency: string | null): string {
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))

  return currency ? `${formatted} ${currency}` : formatted
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    await loadOrders()
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="page-heading">
    <div>
      <h2>{{ t('procurement.deliveries.title') }}</h2>
    </div>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('procurement.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" type="button" @click="load">
      <RefreshCw :size="15" aria-hidden="true" />
      {{ t('procurement.common.retry') }}
    </button>
  </div>

  <template v-else>
    <div class="toolbar">
      <input v-model="search" class="search-input" type="search" :placeholder="t('procurement.deliveries.searchPlaceholder')">
      <label class="check-field">
        <input v-model="openOnly" type="checkbox">
        <span>{{ t('procurement.deliveries.onlyOpen') }}</span>
      </label>
      <div class="summary">
        <span>{{ t('procurement.deliveries.count', { count: visible.length }) }}</span>
      </div>
    </div>

    <div v-if="visible.length === 0" class="panel">
      <span class="empty-inline">{{ t('procurement.deliveries.empty') }}</span>
    </div>

    <template v-else>
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('procurement.deliveries.order') }}</th>
            <th>{{ t('procurement.deliveries.supplier') }}</th>
            <th>{{ t('procurement.deliveries.expected') }}</th>
            <th>{{ t('procurement.deliveries.status') }}</th>
            <th class="cell-qty">{{ t('procurement.orders.total') }}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in paged" :key="order.id">
            <td class="cell-name">
              #{{ order.id }}
              <small class="cell-sub">{{ order.destination_warehouse_name || '—' }}</small>
            </td>
            <td>{{ order.supplier_name || '—' }}</td>
            <td class="cell-date">
              {{ order.expected_date ? formatDisplayDate(order.expected_date) : '—' }}
              <span v-if="urgency(order) === 'overdue'" class="status-badge status-overdue">{{ t('procurement.deliveries.overdue') }}</span>
              <span v-else-if="urgency(order) === 'due'" class="status-badge status-due">{{ t('procurement.deliveries.dueSoon') }}</span>
            </td>
            <td>
              <span :class="['status-badge', purchaseOrderStatusClass(order.status)]">
                {{ t(`procurement.status.${order.status}`) }}
              </span>
            </td>
            <td class="cell-qty">{{ money(order.total_amount, order.currency_code) }}</td>
            <td>
              <RouterLink class="link-action" :to="`/purchase-orders/${order.id}`">
                {{ t('procurement.orders.open') }}
                <ArrowRight :size="13" aria-hidden="true" />
              </RouterLink>
            </td>
          </tr>
        </tbody>
      </table>

      <ListPagination
        :page="page"
        :total-pages="totalPages"
        :total="visible.length"
        :per-page="perPage"
        @update:page="page = $event"
        @update:per-page="perPage = $event"
      />
    </template>
  </template>
</template>
